import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, Popover, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { FormikProps } from "formik";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { titleCase } from "title-case";
import { useMask } from "@react-input/mask";
import { Select, TextField } from "@outpave/ds";
import { fieldErrorText } from "utils/formikFieldError";
import { relationshipLabelKey } from "utils/contacts/relationship";
import ContactSearchAutoComplete from "../../common/ContactSearchAutoComplete";
import AddressField from "components/common/address/AddressField";
import AddPaymentDetailsToContact from "./AddPaymentDetailsToContact";
import {
  EMPTY_CONTACT_ADDRESS,
  FormikContact,
} from "../../../hooks/useContactData";
import ContactType from "../../../enums/ContactType";
import RelationshipType from "../../../enums/RelationshipType";
import FormikCustomFields from "components/common/FormikCustomFields";
import { FormikCustomField } from "hooks/customFields.types";

interface BusinessOption {
  id: string;
  businessName: string;
  website: string;
  status: string;
}

interface HelperTextProps {
  errorText: string;
  onClick?: () => void;
}

const HelperText: React.FC<HelperTextProps> = ({ errorText, onClick }) => {
  return (
    <Typography
      variant="caption"
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        color: "var(--color-text-danger)",
      }}
    >
      {errorText}
    </Typography>
  );
};

interface EditIndividualContactProps {
  contactFormik: FormikProps<FormikContact>;
  customFieldSettings?: FormikCustomField[];
  errorContactFieldAlreadyExists?: string;
  loadingCheckIfContactAlreadyExists?: boolean;
  popoverReactivateAnchorRef?: React.RefObject<HTMLDivElement>;
  popoverExistingContactRef?: React.RefObject<HTMLDivElement>;
  isExternal?: boolean;
  customPaymentDetailsDescription?: string;
  setOpenAddressDrawer: (open: boolean) => void;
  setOpenExistingContactPopover?: (open: boolean) => void;
  handleOpenAddSaveNewBusinessDrawer?: () => void;
  setEmailBlurred?: (blurred: boolean) => void;
  reactivateContact?: (businessId: string) => void;
  handleLinkToBusinessAccount?: (businessContactId: string) => void;
}

const EditIndividualContact: React.FC<EditIndividualContactProps> = ({
  contactFormik,
  customFieldSettings,
  errorContactFieldAlreadyExists = "",
  loadingCheckIfContactAlreadyExists = false,
  popoverReactivateAnchorRef = React.createRef<HTMLDivElement>(),
  popoverExistingContactRef = React.createRef<HTMLDivElement>(),
  isExternal = false,
  customPaymentDetailsDescription = "",
  setOpenAddressDrawer,
  setOpenExistingContactPopover,
  handleOpenAddSaveNewBusinessDrawer,
  setEmailBlurred,
  reactivateContact,
  handleLinkToBusinessAccount,
}) => {
  const { t } = useTranslation();

  const [reactivateAnchorEl, setReactivateAnchorEl] =
    useState<null | HTMLElement>(null);
  const openReactivatePopover = Boolean(reactivateAnchorEl);
  const [selectedBusiness, setSelectedBusiness] =
    useState<BusinessOption | null>(null);

  const handleOpenDeletedBusinessPopover = (business: BusinessOption) => {
    setReactivateAnchorEl(popoverReactivateAnchorRef.current);
    setSelectedBusiness(business);
  };

  // popover from individual reactivating a business
  const handleCloseReactivateBusinessPopover = (reactivate: boolean) => {
    if (selectedBusiness && reactivate && reactivateContact) {
      reactivateContact(selectedBusiness.id);
    } else {
      contactFormik.setFieldValue("businessName", null);
      contactFormik.setFieldValue("parentContactId", null);
    }

    setReactivateAnchorEl(null);
    setSelectedBusiness(null);
  };

  const handleBlurEmail = (e: { target: { name: string } }) => {
    contactFormik.handleBlur(e);
    setEmailBlurred && setEmailBlurred(true);
  };

  const handleClickErrorContactAlreadyExists = () => {
    setOpenExistingContactPopover && setOpenExistingContactPopover(true);
  };

  const phoneMaskRef = useMask({
    mask: "___-___-____",
    replacement: { _: /\d/ },
  });

  return (
    <>
      {contactFormik.values.contactType && (
        <Stack gap="var(--space-6)">
          {/* The anchor stays on a wrapper rather than the field. On a DS
              TextField `ref` reaches the input, so anchoring there would both
              fight the component and put the popover under the text instead of
              under the field. Box forwards refs; Stack and Inline do not. */}
          <Box ref={popoverExistingContactRef}>
            <TextField
              fullWidth
              required
              label={t("Email")}
              name="email"
              value={contactFormik.values.email}
              onChange={contactFormik.handleChange}
              onBlur={handleBlurEmail}
              disabled={isExternal}
              // A node, because the duplicate-contact message is clickable: it
              // is what opens the popover describing the contact already there.
              errorText={
                fieldErrorText(contactFormik, "email") ||
                errorContactFieldAlreadyExists ? (
                  <HelperText
                    errorText={
                      fieldErrorText(contactFormik, "email") ||
                      errorContactFieldAlreadyExists
                    }
                    onClick={
                      errorContactFieldAlreadyExists
                        ? handleClickErrorContactAlreadyExists
                        : undefined
                    }
                  />
                ) : undefined
              }
              trailing={
                loadingCheckIfContactAlreadyExists ? (
                  <CircularProgress size={16} />
                ) : undefined
              }
            />
          </Box>

          <Inline gap="var(--space-4)" align="start" wrap={false}>
            <TextField
              fullWidth
              required
              label={t("First Name")}
              name="firstName"
              value={contactFormik.values.firstName}
              onChange={contactFormik.handleChange}
              onBlur={contactFormik.handleBlur}
              errorText={fieldErrorText(contactFormik, "firstName")}
            />

            <TextField
              fullWidth
              required
              label={t("Last Name")}
              name="lastName"
              value={contactFormik.values.lastName}
              onChange={contactFormik.handleChange}
              onBlur={contactFormik.handleBlur}
              errorText={fieldErrorText(contactFormik, "lastName")}
            />
          </Inline>

          <Inline gap="var(--space-4)" align="start" wrap={false}>
            <TextField
              fullWidth
              label={t("Phone")}
              name="phone"
              value={contactFormik.values.phone}
              onChange={contactFormik.handleChange}
              onBlur={contactFormik.handleBlur}
              errorText={fieldErrorText(contactFormik, "phone")}
              // `ref`, not `inputRef`: the DS field spreads `...rest` into
              // InputBase and then sets `inputRef` from its own forwarded ref,
              // so the later key wins and an `inputRef` here is discarded,
              // which silently kills the mask.
              ref={phoneMaskRef}
            />

            <TextField
              fullWidth
              label={t("Title")}
              name="title"
              value={contactFormik.values.title}
              onChange={contactFormik.handleChange}
              onBlur={contactFormik.handleBlur}
              errorText={fieldErrorText(contactFormik, "title")}
            />
          </Inline>

          <AddressField
            label={t("Address")}
            name="address"
            address={contactFormik.values.address}
            onOpen={() => setOpenAddressDrawer(true)}
            onClear={() =>
              contactFormik.setFieldValue("address", EMPTY_CONTACT_ADDRESS)
            }
            errorText={fieldErrorText(contactFormik, "address.addressName")}
          />

          {!isExternal && (
            <Select
              fullWidth
              required
              // relationshipType is RelationshipTypeEnum! and updateContact
              // drops a falsy one from the mutation, so a cleared value leaves
              // the record alone while the form shows empty.
              clearable={false}
              label={t("Relationship Type")}
              value={contactFormik.values.relationshipType || null}
              options={Object.values(RelationshipType).map((type) => ({
                id: type,
                label: t(relationshipLabelKey(type) ?? type),
              }))}
              onChange={(value) =>
                value && contactFormik.setFieldValue("relationshipType", value)
              }
              errorText={fieldErrorText(contactFormik, "relationshipType")}
              helperText={t("Define this contact's role with your business")}
            />
          )}

          {!isExternal && (
            <div ref={popoverReactivateAnchorRef}>
              <ContactSearchAutoComplete
                contactId={contactFormik.values.parentContactId}
                contactType={ContactType.Business}
                showAddContactInDropdown={true}
                onSelectContact={(contact: any) => {
                  if (contact?.status === "DELETED") {
                    handleOpenDeletedBusinessPopover(contact);
                  }
                  contactFormik.setFieldValue(
                    "businessName",
                    contact?.businessName,
                  );
                  contactFormik.setFieldValue("parentContactId", contact?.id);
                }}
                customAddContactInDropdownFunction={() =>
                  handleOpenAddSaveNewBusinessDrawer &&
                  handleOpenAddSaveNewBusinessDrawer()
                }
                customPlaceholder={t("Attach to Business")}
                helperText={t(
                  "Attach this contact to an existing business contact?",
                )}
              />
            </div>
          )}

          {!isExternal && (
            <FormikCustomFields
              settings={customFieldSettings}
              formik={contactFormik}
              variant="flat"
            />
          )}

          <AddPaymentDetailsToContact
            contactFormik={contactFormik}
            linkToBusinessAccount={handleLinkToBusinessAccount}
            {...(customPaymentDetailsDescription && {
              customDescription: customPaymentDetailsDescription,
            })}
          />
        </Stack>
      )}

      <Popover
        id={"reactivate-popover"}
        open={openReactivatePopover}
        anchorEl={reactivateAnchorEl}
        onClose={() => handleCloseReactivateBusinessPopover(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Stack sx={{ padding: "var(--space-5)", width: 512 }}>
          <Stack gap="var(--space-1)" sx={{ textAlign: "center" }}>
            <Typography variant="body1">
              {t("This business contact is inactive")}
            </Typography>
            <Typography variant="body1">
              {t("Do you want to reactivate it?")}
            </Typography>
          </Stack>
          <Inline justify="center" gap="var(--space-7)">
            <Button
              variant="primary-filled"
              onClick={() => handleCloseReactivateBusinessPopover(true)}
            >
              {t("Yes")}
            </Button>
            <Button
              variant="secondary-outlined"
              onClick={() => handleCloseReactivateBusinessPopover(false)}
            >
              {t("No")}
            </Button>
          </Inline>
        </Stack>
      </Popover>
    </>
  );
};

export default EditIndividualContact;
